import React, {useEffect, useState} from 'react';
import {View, Modal, ScrollView, Platform} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';

import Header from '../../elements/Header';
import Text from '../../elements/Text';
import Checkbox from '../../elements/Checkbox';
import Divider from '../../elements/Divider';
import Button from '../../elements/Button';
import RangeSlider from '../../elements/RangeSlider';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../../theme';
import {BUTTON_TYPES} from '../../../utils/Constants';
import {fetchApi} from '../../../utils/ApiHelper';
import {HTTP_METHODS, PRODUCTS_COUNT} from '../../../utils/URLConstants';
import CommonStyles from '../../../utils/CommonStyles';
import {useDisableBodyScroll} from '../../../hooks/use-disable-dody-scroll';
import {tID} from '../../../utils/TestIdHelper';
import {CloseIcon} from '../../../utils/SvgIcons';

export const Container = ({children, onClose = () => {}}) => {
  const styles = useThemedStyles(makeStyles);
  return Platform.OS === 'web' ? (
    <View
      testID={tID('FilterProductsModal-View-WebContainer')}
      onStartShouldSetResponder={onClose}
      style={styles.modalContianer}>
      <View
        testID={tID('FilterProductsModal-View-WebFiltersContainer')}
        style={styles.mainContainer}
        onStartShouldSetResponder={e => e.stopPropagation()}>
        {children}
      </View>
    </View>
  ) : (
    <View
      testID={tID('FilterProductsModal-View-MobileContainer')}
      style={CommonStyles.full_flex}>
      <SafeAreaView style={styles.whiteBackground} />
      {children}
      <SafeAreaView />
    </View>
  );
};

const productCategories = [
  'All',
  'Free For Me',
  // 'Bags',
  // 'Purchase Available',
  // 'Supplies',
];

// const colors = ['Black', 'Red', 'Blue', 'Yellow', 'Green', 'White', 'Purple'];

const FilterProductsModal = ({
  visible,
  onClose,
  onApplyFilter,
  selectedFilters,
  onResetFilter,
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const [rangeValue, setRangeValue] = useState([0, 50]);
  const [maxValue, setMaxValue] = useState(50);
  const [scrollEnable, setScrollEnable] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [productCount, setProductCount] = useState([
    8, 50, 25, 40, 85, 85, 91, 35, 53, 24,
  ]);

  //hook to disable scrolling for web
  useDisableBodyScroll(visible);

  useEffect(() => {
    getFilterData();
  }, []);

  useEffect(() => {
    if (visible) {
      if (selectedFilters.length > 0) {
        if (selectedFilters[0].length > 0) {
          setRangeValue(selectedFilters[0]);
        } else {
          setRangeValue([0, maxValue]);
        }
        setSelectedCategories(selectedFilters[1]);
      } else {
        setRangeValue([0, maxValue]);
        setSelectedCategories([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot filters only when the modal opens; re-firing on maxValue/selectedFilters would reset the user's in-progress edits
  }, [visible]);

  const getFilterData = () => {
    fetchApi(HTTP_METHODS.GET, PRODUCTS_COUNT)
      .then(response => {
        if (response.status === 200) {
          let data = response.data.data;
          let highestPriceRange = data[data.length - 1].range;
          let maxPrice = parseInt(highestPriceRange.split('-')[1]);
          setMaxValue(maxPrice + 10);
          setRangeValue([0, maxPrice + 10]);
          let count = data.map(i => i.count);
          setProductCount(count);
        }
      })
      .catch(error => {
        // DLog(error);
      });
  };

  const onPressColor = item => {
    let selected = [...selectedColors];
    let idx = selected.indexOf(item);
    if (idx == -1) {
      selected.push(item);
    } else {
      const _ = selected.splice(idx, 1);
    }
    setSelectedColors(selected);
  };

  const onPressCategory = (item, index) => {
    // let selected = [...selectedCategories];
    // let idx = selected.indexOf(item);
    // if (idx == -1) {
    //   selected.push(item);
    // } else {
    //   const _ = selected.splice(idx, 1);
    // }
    const selected = [index];
    setSelectedCategories(selected);
  };

  const renderCategories = () => {
    return (
      <View
        testID={tID('FilterProductsModal-View-CategoriesContainer')}
        style={styles.paddingHorizontal24}>
        <Text
          variation="Body1SemiBold"
          color={colors.textPrimary}
          topMargin={24}>
          Categories{' '}
          <Text
            testID={tID('FilterProductsModal-Text-CategoriesCount')}
            variation="Body1Regular"
            color={colors.textPrimary}>
            ({selectedCategories.length})
          </Text>
        </Text>
        <View
          testID={tID('FilterProductsModal-View-SelectedCategories')}
          style={styles.filterContainer}>
          {productCategories.map((item, index) => {
            return (
              <View
                testID={tID(
                  `FilterProductsModal-View-FilterCategory${index + 1}`,
                )}
                key={index}
                style={styles.itemContainer(index)}>
                <Checkbox
                  selected={selectedCategories.includes(index)}
                  onPress={() => onPressCategory(item, index)}
                  title={item}
                  topMargin={0}
                  numberOfLines={1}
                  titleStyle={styles.title}
                  checkboxStyle={Platform.OS === 'web' && styles.checkbox}
                />
              </View>
            );
          })}
        </View>
        <Divider top={16} color={colors.border} />
      </View>
    );
  };

  const renderPriceRangeSlider = () => {
    return (
      <View style={styles.paddingHorizontal24}>
        <Text
          variation="Body1SemiBold"
          color={colors.textPrimary}
          topMargin={24}>
          Price Range
        </Text>
        <Text variation="Text1Regular" color={colors.textMuted} topMargin={8}>
          By product availability
        </Text>
        <RangeSlider
          max={maxValue}
          values={rangeValue}
          areaData={productCount}
          onValuesUpdated={values => setRangeValue(values.values)}
          onSliderDragStart={() => setScrollEnable(false)}
          onSliderDragEnd={() => setScrollEnable(true)}
        />
        {/* <Divider top={72} color={COLORS.greyLight} /> */}
      </View>
    );
  };

  // const renderColor = () => {
  //   return (
  //     <View style={styles.paddingHorizontal24}>
  //       <Text variation="Body1SemiBold" color={COLORS.black} topMargin={24}>
  //         Color{' '}
  //         <Text variation="Body1Regular" color={COLORS.black}>
  //           (2)
  //         </Text>
  //       </Text>
  //       <View style={styles.filterContainer}>
  //         {colors.map((item, index) => {
  //           return (
  //             <View key={index} style={styles.itemContainer(index)}>
  //               <Checkbox
  //                 selected={selectedColors.includes(item)}
  //                 onPress={() => onPressColor(item)}
  //                 title={item}
  //                 topMargin={0}
  //                 numberOfLines={1}
  //                 titleStyle={styles.title}
  //               />
  //             </View>
  //           );
  //         })}
  //       </View>
  //     </View>
  //   );
  // };

  return (
    <Modal
      testID={tID('FilterProductsModal-Modal-Main')}
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
      transparent={Platform.OS === 'web'}>
      <Container onClose={onClose}>
        <View
          testID={tID('FilterProductsModal-View-HeaderContainer')}
          style={styles.headerComponent}>
          <Header
            title="Filter Options"
            leftImage={<CloseIcon />}
            leftIconAccessibilityLabel="Close"
            onPressLeftIcon={onClose}
          />
        </View>
        <View
          testID={tID('FilterProductsModal-View-Filters')}
          style={styles.container}>
          <ScrollView
            testID={tID('FilterProductsModal-ScrollView-Filters')}
            scrollEnabled={scrollEnable}
            style={styles.leftButton}>
            {renderCategories()}
            {renderPriceRangeSlider()}
            {/* {renderColor()} */}
          </ScrollView>

          <View
            testID={tID('FilterProductsModal-View-ButtonsContainer')}
            style={styles.buttonsContainer}>
            <Button
              testID={tID('FilterProductsModal-Button-Reset')}
              title={'Reset'}
              buttonType={BUTTON_TYPES.Bordered}
              style={styles.leftButton}
              onPress={() => {
                setRangeValue([0, maxValue]);
                setSelectedCategories([]);
                onResetFilter();
              }}
            />
            <Button
              testID={tID('FilterProductsModal-Button-Apply')}
              title={'Apply'}
              // disabled={selectedTags.length === 0}
              titleColor={colors.textOnAccent}
              buttonType={BUTTON_TYPES.Filled}
              style={styles.rightButton}
              onPress={() => {
                let priceRange = rangeValue;
                if (rangeValue[0] == 0 && rangeValue[1] == 50) {
                  priceRange = [];
                }
                onApplyFilter([priceRange, selectedCategories]);
              }}
            />
          </View>
        </View>
      </Container>
    </Modal>
  );
};

export default FilterProductsModal;
